import React, { useState, useMemo, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Page, Text, View, Document, PDFViewer, StyleSheet } from "@react-pdf/renderer";
import { useMaterialUIController } from "context";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  FP,
  PageHeader,
  StatCard,
  fpDataGridSx,
  fpInputSx,
} from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import { useMultiempresa, SelectorEmpresa, nombreEmpresa } from "lib/multiempresa";
import { puedeAccionPrograma, tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { imprimirTicketRecibo } from "modules/pagos/mantenimientopagos";
import { usePortalQr } from "lib/portalqr";

/**
 * @param embedded  Renderiza sin PageHeader ni fondo propio para usarse como
 *                  listado dentro de otra pantalla (p.ej. Pagos). En este
 *                  modo carga automáticamente el mes en curso.
 * @param refreshToken  Al cambiar su valor se recarga el listado (la pantalla
 *                      contenedora lo incrementa tras cobrar un pago).
 */
function Informepagos({ embedded = false, refreshToken = 0 } = {}) {
  const hoyISO = () => new Date().toISOString().slice(0, 10);
  const inicioMesISO = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  };
  const [fecha1, setFecha1] = useState(embedded ? inicioMesISO() : "");
  const [fecha2, setFecha2] = useState(embedded ? hoyISO() : "");
  const [nosucursal, setNosucursal] = useState("");
  const [rows, setRows] = useState([]);
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pagoTarget, setPagoTarget] = useState(null);
  // Candado síncrono: el estado tarda un render y un doble clic mandaba
  // dos anulaciones del mismo recibo (préstamo 37 de García Martínez).
  const cancelandoRef = useRef(false);
  const [cancelandoPago, setCancelandoPago] = useState(false);
  const [filtroNombre, setFiltroNombre] = useState("");

  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  // Cancelar un pago exige ares4010 con borrado (el backend re-valida).
  const puedeCancelar = puedeAccionPrograma(roleDetalles, "ares4010", "borrar");
  // Reimprimir el recibo tiene permiso propio. Antes exigía ares4030 —la
  // pantalla que ANULA pagos—, así que para dejar reimprimir había que
  // conceder también el poder de anular.
  const puedeReimprimir = tienePermisosModulo(roleDetalles, "reimprimirpagos");
  const [companiaTicket, setCompaniaTicket] = useState(null);
  const [reimprimiendo, setReimprimiendo] = useState(null); // norecibo en curso

  // ---- Estado del listado: pagos vigentes o cancelados ----
  // Vigentes usa /api/informespagos; cancelados /api/informespagoscancelados
  // (antes pantalla aparte "Informe Pagos Cancelados").
  const puedeVigentes =
    tienePermisosModulo(roleDetalles, "informepagos") ||
    tienePermisosModulo(roleDetalles, "ares7050") ||
    tienePermisosModulo(roleDetalles, "ares4010");
  const puedeVerCancelados = tienePermisosModulo(roleDetalles, "informepagoscancelados");
  const [estadoPagos, setEstadoPagos] = useState(puedeVigentes ? "vigentes" : "cancelados");
  // Estado con el que se cargó el listado actual (decide columnas y títulos).
  const [estadoCargado, setEstadoCargado] = useState(puedeVigentes ? "vigentes" : "cancelados");
  const esCancelados = estadoCargado === "cancelados";
  // Cobros de financiamientos de motores (General) o de préstamos personales
  // (prestamo.ppersonal): mismo flag que Antigüedad de Saldo.
  const [prestamosPersonales, setPrestamosPersonales] = useState(false);
  const { post } = useApi();
  // QR del portal al pie del recibo reimpreso.
  usePortalQr();
  const sucursal = useSucursales();
  // Multi-empresa: filtro y columna (solo con el modo activo)
  const multiempresa = useMultiempresa();
  const [empresaFiltro, setEmpresaFiltro] = useState("0");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const columns = useMemo(
    () => [
      { field: "noprest", headerName: "DOC #", width: 100, align: "center", headerAlign: "center" },
      { field: "fecha", headerName: "Fecha", width: 110, align: "center", headerAlign: "center" },
      // Multi-empresa: empresa del recibo (solo con el modo activo)
      ...(multiempresa.activo
        ? [
            {
              field: "empresa",
              headerName: "Empresa",
              width: 130,
              valueGetter: (value, row) => nombreEmpresa(multiempresa.empresas, row.empresa || 1),
            },
          ]
        : []),
      { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 160 },
      { field: "valor", headerName: "Valor", width: 130 },
      {
        field: "vpagcap",
        headerName: "Capital",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
        
      },
      {
        field: "vpagint",
        headerName: "Interés",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "vpagmora",
        headerName: "Mora",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
      },
      ...(puedeCancelar || puedeReimprimir
        ? [
            {
              field: "acciones",
              headerName: "Acciones",
              width: 100,
              sortable: false,
              filterable: false,
              align: "center",
              headerAlign: "center",
              renderCell: (params) => (
                <Stack direction="row" spacing={0.5}>
                  {puedeReimprimir && (
                    <IconButton
                      size="small"
                      disabled={reimprimiendo !== null}
                      onClick={() => reimprimir(params.row)}
                    >
                      <PrintOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                  {puedeCancelar && (
                    <IconButton size="small" color="error" onClick={() => abrirConfirmacion(params.row)}>
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [puedeCancelar, puedeReimprimir, reimprimiendo, multiempresa]
  );

  // Columnas del listado de cancelados (pagoscanc: recibo, cuota, plazo, interés).
  const columnasCanceladas = useMemo(
    () => [
      { field: "id", headerName: "Recibo #", width: 110, align: "center", headerAlign: "center" },
      { field: "fecha", headerName: "Fecha", width: 110, align: "center", headerAlign: "center" },
      { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 140 },
      { field: "apellidos", headerName: "Apellidos", flex: 1, minWidth: 140 },
      { field: "cantsol", headerName: "Cuota", width: 130, align: "right", headerAlign: "right" },
      { field: "plazo", headerName: "Plazo", width: 100 },
      { field: "interes", headerName: "Interés", width: 110, align: "right", headerAlign: "right" },
    ],
    []
  );

  async function procesarrequerimiento(estadoOverride, personalesOverride) {
    const estadoConsulta = estadoOverride ?? estadoPagos;
    const endpoint =
      estadoConsulta === "cancelados" ? "/api/informespagoscancelados" : "/api/informespagos";
    const { status, data } = await post(endpoint, {
      fechadesde: fecha1,
      fechahasta: fecha2,
      sucursal: nosucursal,
      prestamospersonales: personalesOverride ?? prestamosPersonales,
      ...(multiempresa.activo ? { empresa: Number(empresaFiltro) } : {}),
    });
    if (status === 200) {
      const rawRows = Array.isArray(data?.prestamo)
        ? data.prestamo
        : Array.isArray(data?.prestamos)
        ? data.prestamos
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      const normalizedRows = rawRows.map((item, index) => ({
        ...item,
        id: item?.id ?? item?.doc ?? item?.documento ?? item?.prestamo ?? `row-${index}`,
      }));
      setRows(normalizedRows);
      setFiltroNombre("");
      setEstadoCargado(estadoConsulta);
    }
    if (status === 400) {
      setRows([]);
      const msg = data["Error"] || "Solicitud inválida";
      // En modo embebido la carga es automática: un rango sin pagos no debe
      // interrumpir con un alert, la tabla vacía ya lo comunica.
      if (!(embedded && /^no hay/i.test(msg))) alert(msg);
    }
  }

  // En modo embebido el listado carga solo (al montar y cuando la pantalla
  // contenedora incrementa refreshToken tras cobrar o cancelar un pago).
  useEffect(() => {
    if (embedded && fecha1 && fecha2) procesarrequerimiento();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, refreshToken]);

  function abrirConfirmacion(row) {
    setPagoTarget(row);
    setConfirmOpen(true);
  }

  async function cancelarPago() {
    if (!pagoTarget || cancelandoRef.current) return;
    cancelandoRef.current = true;
    setCancelandoPago(true);
    try {
      const { status, data } = await post("/api/cancelarpagos", {
        norecibo: pagoTarget.norecibo || pagoTarget.id,
        noprest: pagoTarget.noprest,
        sucursal: pagoTarget.sucursal || "",
        tipodeusuario: "",
      });
      if (status === 200) {
        setRows((prev) =>
          prev.filter(
            (r) =>
              !(
                r.id === pagoTarget.id ||
                (r.norecibo === pagoTarget.norecibo && r.noprest === pagoTarget.noprest)
              )
          )
        );
        setConfirmOpen(false);
        setPagoTarget(null);
        alert("Pago cancelado");
      } else {
        alert(data?.Error || "No se pudo cancelar el pago");
        setConfirmOpen(false);
      }
    } catch (err) {
      alert("Error de red: " + err.message);
      setConfirmOpen(false);
    } finally {
      cancelandoRef.current = false;
      setCancelandoPago(false);
    }
  }

  // Reimpresión del recibo (reimprimirpagos): mismo ticket/endpoint que
  // Mantenimiento Pagos. El membrete sale del perfil de cobro (cacheado) y,
  // en multi-empresa, del que devuelve el backend para la empresa del pago.
  async function reimprimir(row) {
    const norecibo = row.norecibo || row.id;
    setReimprimiendo(norecibo);
    try {
      let comp = companiaTicket;
      if (!comp) {
        const { ok: okPerfil, data: perfil } = await post("/api/cobropago_perfil", {});
        comp = okPerfil ? perfil.compania || null : null;
        if (comp) setCompaniaTicket(comp);
      }
      const { ok, data } = await post("/api/ares4030_reimprimirrecibo", { norecibo });
      if (ok && data.recibo) imprimirTicketRecibo(data.recibo, data.compania || comp);
      else alert(data?.Error || "No se pudo reimprimir el recibo");
    } catch (err) {
      alert("Error de red: " + err.message);
    } finally {
      setReimprimiendo(null);
    }
  }

  const rowsFiltradas = useMemo(() => {
    const q = filtroNombre.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const n = String(r.nombres ?? "").toLowerCase();
      const a = String(r.apellidos ?? "").toLowerCase();
      return n.includes(q) || a.includes(q);
    });
  }, [rows, filtroNombre]);

  const fmtMoney = (v) =>
    Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const totalCapital = rowsFiltradas.reduce(
    (acc, r) => acc + parseFloat(String(r.vpagcap || "0").replace(/,/g, "")),
    0
  );
  const totalInteres = rowsFiltradas.reduce(
    (acc, r) => acc + parseFloat(String(r.vpagint || "0").replace(/,/g, "")),
    0
  );
  const totalMora = rowsFiltradas.reduce(
    (acc, r) => acc + parseFloat(String(r.vpagmora || "0").replace(/,/g, "")),
    0
  );

  function handlepdf() {
    if (rowsFiltradas.length === 0) {
      alert("No hay datos para procesar este reporte");
    } else {
      setPresentarpdf(true);
    }
  }

  // ---- PDF del informe: encabezado con período, resumen y tabla totalizada ----
  // react-pdf no aplica fontWeight a Helvetica: la negrita es la fuente Helvetica-Bold.
  const num = (v) => parseFloat(String(v ?? 0).replace(/,/g, "").trim()) || 0;
  const fechaCorta = (iso) => {
    const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || "";
  };
  const PDF_C = {
    navy: "#0E1F33",
    navySoft: "#16314F",
    accent: "#1B6CFB",
    zebra: "#F4F6FB",
    border: "#E6EAF1",
    text: "#0F172A",
    muted: "#5A6B85",
    green: "#047857",
  };
  const pdfStyles = StyleSheet.create({
    page: { paddingTop: 28, paddingBottom: 48, paddingHorizontal: 28, fontFamily: "Helvetica", fontSize: 8, color: PDF_C.text, backgroundColor: "#FFFFFF" },
    header: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", paddingBottom: 8, borderBottomWidth: 2, borderBottomColor: PDF_C.navy, marginBottom: 10 },
    negocio: { fontFamily: "Helvetica-Bold", fontSize: 13, color: PDF_C.navy, textTransform: "uppercase" },
    titulo: { fontSize: 10, color: PDF_C.accent, marginTop: 3, fontFamily: "Helvetica-Bold" },
    meta: { fontSize: 7.5, color: PDF_C.muted, textAlign: "right", lineHeight: 1.4 },
    metaFuerte: { fontFamily: "Helvetica-Bold", color: PDF_C.text },
    resumen: { flexDirection: "row", marginBottom: 12 },
    tarjeta: { flex: 1, borderWidth: 1, borderColor: PDF_C.border, borderRadius: 4, paddingVertical: 6, paddingHorizontal: 8, marginRight: 6 },
    tarjetaFinal: { flex: 1.2, borderRadius: 4, paddingVertical: 6, paddingHorizontal: 8, backgroundColor: PDF_C.navy },
    tarjetaEtiqueta: { fontSize: 6.5, color: PDF_C.muted, textTransform: "uppercase", letterSpacing: 0.5 },
    tarjetaValor: { fontFamily: "Helvetica-Bold", fontSize: 10.5, marginTop: 3, color: PDF_C.text },
    thead: { flexDirection: "row", backgroundColor: PDF_C.navy, color: "#FFFFFF", paddingVertical: 5, borderTopLeftRadius: 3, borderTopRightRadius: 3 },
    th: { fontFamily: "Helvetica-Bold", fontSize: 7.5, paddingHorizontal: 3 },
    fila: { flexDirection: "row", paddingVertical: 3.5, borderBottomWidth: 0.5, borderBottomColor: PDF_C.border },
    td: { fontSize: 7.5, paddingHorizontal: 3 },
    totales: { flexDirection: "row", backgroundColor: PDF_C.zebra, paddingVertical: 6, borderTopWidth: 1.5, borderTopColor: PDF_C.navy, borderBottomWidth: 1.5, borderBottomColor: PDF_C.navy },
    tdTotal: { fontFamily: "Helvetica-Bold", fontSize: 8, paddingHorizontal: 3, color: PDF_C.navy },
    pie: { position: "absolute", bottom: 20, left: 28, right: 28, flexDirection: "row", justifyContent: "space-between", fontSize: 7, color: PDF_C.muted, borderTopWidth: 0.5, borderTopColor: PDF_C.border, paddingTop: 5 },
  });

  const MyDocument = ({ rowsData, nombreNegocio1, cancelados = false }) => {
    const data = rowsData || [];
    // Columnas: ancho en puntos (A4 vertical útil ≈ 539 pt).
    const cols = cancelados
      ? [
          { key: "recibo", label: "Recibo #", w: 60, get: (r) => r.id },
          { key: "fecha", label: "Fecha", w: 62, get: (r) => r.fecha },
          { key: "cliente", label: "Cliente", w: 237, get: (r) => `${r.nombres ?? ""} ${r.apellidos ?? ""}`.trim() },
          { key: "cuota", label: "Cuota", w: 80, right: true, money: true, get: (r) => num(r.cantsol) },
          { key: "plazo", label: "Plazo", w: 45, right: true, get: (r) => r.plazo ?? "" },
          { key: "interes", label: "Interés", w: 55, right: true, get: (r) => r.interes ?? "" },
        ]
      : [
          { key: "prest", label: "Préstamo", w: 44, get: (r) => r.noprest ?? "" },
          { key: "recibo", label: "Recibo", w: 40, get: (r) => r.norecibo ?? "" },
          { key: "fecha", label: "Fecha", w: 50, get: (r) => r.fecha },
          { key: "cliente", label: "Cliente", w: 125, get: (r) => r.nombres ?? "" },
          { key: "cuota", label: "Cuota", w: 56, right: true, money: true, get: (r) => num(r.cantsol) },
          { key: "cap", label: "Capital", w: 56, right: true, money: true, get: (r) => num(r.vpagcap) },
          { key: "int", label: "Interés", w: 54, right: true, money: true, get: (r) => num(r.vpagint) },
          { key: "mora", label: "Mora", w: 48, right: true, money: true, get: (r) => num(r.vpagmora) },
          { key: "total", label: "Total cobrado", w: 66, right: true, money: true, bold: true, get: (r) => num(r.vpagcap) + num(r.vpagint) + num(r.vpagmora) },
        ];
    const totales = {};
    cols.filter((c) => c.money).forEach((c) => {
      totales[c.key] = data.reduce((s, r) => s + c.get(r), 0);
    });

    const resumen = cancelados
      ? [
          { label: "Recibos", valor: data.length.toLocaleString("en-US") },
          { label: "Total cuotas", valor: fmtMoney(totales.cuota), final: true },
        ]
      : [
          { label: "Recibos", valor: data.length.toLocaleString("en-US") },
          { label: "Capital", valor: fmtMoney(totales.cap) },
          { label: "Interés", valor: fmtMoney(totales.int) },
          { label: "Mora", valor: fmtMoney(totales.mora) },
          { label: "Total cobrado", valor: fmtMoney(totales.total), final: true },
        ];

    const periodo =
      fecha1 || fecha2 ? `${fechaCorta(fecha1)} al ${fechaCorta(fecha2)}` : "Todas las fechas";
    const sucursalTxt = nosucursal || "Todas";
    const empresaTxt =
      multiempresa.activo && empresaFiltro !== "0"
        ? nombreEmpresa(multiempresa.empresas, Number(empresaFiltro))
        : null;
    const generado = new Date().toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" });
    const celda = (c, extra) => ({ width: c.w, textAlign: c.right ? "right" : "left", ...extra });

    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%", colorScheme: "light", background: "#fff" }}>
        <Document title={cancelados ? "Informe de pagos cancelados" : "Informe de pagos"}>
          <Page size="A4" orientation="portrait" wrap style={pdfStyles.page}>
            <View style={pdfStyles.header} fixed>
              <View>
                <Text style={pdfStyles.negocio}>{nombreNegocio1 || "Empresa"}</Text>
                <Text style={pdfStyles.titulo}>
                  {cancelados ? "Informe de Pagos Cancelados" : "Informe de Pagos Realizados"}
                  {prestamosPersonales ? " · Préstamos personales" : ""}
                </Text>
              </View>
              <View>
                <Text style={pdfStyles.meta}>
                  Período: <Text style={pdfStyles.metaFuerte}>{periodo}</Text>
                </Text>
                <Text style={pdfStyles.meta}>
                  Sucursal: <Text style={pdfStyles.metaFuerte}>{sucursalTxt}</Text>
                </Text>
                {empresaTxt && (
                  <Text style={pdfStyles.meta}>
                    Empresa: <Text style={pdfStyles.metaFuerte}>{empresaTxt}</Text>
                  </Text>
                )}
                {filtroNombre.trim() !== "" && (
                  <Text style={pdfStyles.meta}>
                    Cliente contiene: <Text style={pdfStyles.metaFuerte}>{filtroNombre.trim()}</Text>
                  </Text>
                )}
              </View>
            </View>

            <View style={pdfStyles.resumen}>
              {resumen.map((t, i) => (
                <View
                  key={t.label}
                  style={[t.final ? pdfStyles.tarjetaFinal : pdfStyles.tarjeta, i === resumen.length - 1 ? { marginRight: 0 } : {}]}
                >
                  <Text style={[pdfStyles.tarjetaEtiqueta, t.final ? { color: "#C7D2E3" } : {}]}>{t.label}</Text>
                  <Text style={[pdfStyles.tarjetaValor, t.final ? { color: "#FFFFFF" } : {}]}>{t.valor}</Text>
                </View>
              ))}
            </View>

            <View style={pdfStyles.thead} fixed>
              {cols.map((c) => (
                <Text key={c.key} style={[pdfStyles.th, celda(c)]}>
                  {c.label}
                </Text>
              ))}
            </View>
            {data.map((r, i) => (
              <View key={i} wrap={false} style={[pdfStyles.fila, { backgroundColor: i % 2 === 1 ? PDF_C.zebra : "#FFFFFF" }]}>
                {cols.map((c) => {
                  const v = c.get(r);
                  return (
                    <Text
                      key={c.key}
                      style={[pdfStyles.td, celda(c), c.bold ? { fontFamily: "Helvetica-Bold", color: PDF_C.navy } : {}]}
                    >
                      {c.money ? fmtMoney(v) : String(v ?? "")}
                    </Text>
                  );
                })}
              </View>
            ))}
            <View style={pdfStyles.totales} wrap={false}>
              {cols.map((c, idx) => (
                <Text key={c.key} style={[pdfStyles.tdTotal, celda(c)]}>
                  {c.money
                    ? fmtMoney(totales[c.key])
                    : idx === 0
                    ? "TOTALES"
                    : ""}
                </Text>
              ))}
            </View>

            <View style={pdfStyles.pie} fixed>
              <Text>Generado el {generado}</Text>
              <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  const exportBtn = (
    <Button
      variant="contained"
      startIcon={<FileDownloadRoundedIcon />}
      disabled={rowsFiltradas.length === 0}
      onClick={handlepdf}
      sx={{
        bgcolor: FP.color.navy,
        color: "#FFFFFF",
        borderRadius: 1.4,
        px: 2.2,
        height: 38,
        boxShadow: "none",
        textTransform: "none",
        fontWeight: 700,
        "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
        "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
      }}
    >
      Export Data
    </Button>
  );

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
        {!embedded && (
          <PageHeader
            title="Pagos"
            subtitle="Informe de Pagos Realizados"
            cta={exportBtn}
          />
        )}

        <Box sx={{ p: embedded ? 0 : { xs: 2, sm: 2.5, md: 3 } }}>
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: "#FAFBFE",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
                Filtros de Búsqueda
              </Typography>
              <Typography sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
                Rango · Tipo · Sucursal
              </Typography>
              {embedded && exportBtn}
            </Box>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="Fecha Desde"
                    value={fecha1}
                    onChange={(e) => setFecha1(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="Fecha Hasta"
                    value={fecha2}
                    onChange={(e) => setFecha2(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                {puedeVigentes && puedeVerCancelados && (
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Estado"
                      value={estadoPagos}
                      onChange={(e) => {
                        setEstadoPagos(e.target.value);
                        if (fecha1 && fecha2) procesarrequerimiento(e.target.value);
                      }}
                      sx={fpInputSx}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="vigentes">Realizados</option>
                      <option value="cancelados">Cancelados</option>
                    </TextField>
                  </Grid>
                )}
                {/* General = financiamientos de motores; Personal = préstamos
                    personales (mismo corte que Antigüedad de Saldo). */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de préstamo"
                    value={prestamosPersonales ? "personal" : "general"}
                    onChange={(e) => {
                      const esPersonal = e.target.value === "personal";
                      setPrestamosPersonales(esPersonal);
                      if (fecha1 && fecha2) procesarrequerimiento(undefined, esPersonal);
                    }}
                    sx={fpInputSx}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="general">General</option>
                    <option value="personal">Personal</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    disablePortal
                    options={sucursal || []}
                    value={nosucursal || null}
                    onChange={(event, value) => setNosucursal(value?.label || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sucursal"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        sx={fpInputSx}
                      />
                    )}
                    fullWidth
                  />
                </Grid>
                {multiempresa.activo && (
                  <Grid item xs={12} sm={6} md={3}>
                    <SelectorEmpresa
                      value={empresaFiltro}
                      onChange={setEmpresaFiltro}
                      empresas={multiempresa.empresas}
                      sx={fpInputSx}
                    />
                  </Grid>
                )}
              </Grid>
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => procesarrequerimiento()}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 200 },
                    height: 46,
                    bgcolor: FP.color.navy,
                    color: "#FFFFFF",
                    borderRadius: 1.4,
                    textTransform: "none",
                    fontSize: "0.94rem",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  }}
                >
                  Procesar
                </Button>
              </Box>
            </CardContent>
          </Card>

          {rows.length > 0 && !esCancelados && (
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 2,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              <StatCard
                label="Total registros"
                value={String(rowsFiltradas.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
              <StatCard
                label="Total Capital"
                value={`$${fmtMoney(totalCapital)}`}
                icon={AccountBalanceWalletOutlinedIcon}
                accent={FP.color.navy}
              />
              <StatCard
                label="Total Interés"
                value={`$${fmtMoney(totalInteres)}`}
                icon={TrendingUpOutlinedIcon}
                accent={FP.color.accentBlue}
              />
              <StatCard
                label="Total Mora"
                value={`$${fmtMoney(totalMora)}`}
                icon={WarningAmberOutlinedIcon}
                accent={FP.color.danger}
              />
            </Box>
          )}

          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: "#FAFBFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
                {esCancelados ? "Listado de pagos cancelados" : "Listado de pagos realizados"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                {rows.length > 0 && (
                  <TextField
                    size="small"
                    placeholder="Buscar por nombre o apellido"
                    value={filtroNombre}
                    onChange={(e) => setFiltroNombre(e.target.value)}
                    sx={{
                      minWidth: { xs: "100%", sm: 260 },
                      "& .MuiOutlinedInput-root": {
                        height: 36,
                        borderRadius: 1.2,
                        bgcolor: "#FFFFFF",
                        fontSize: "0.86rem",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                {rowsFiltradas.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => handlepdf()}
                    sx={{
                      borderColor: FP.color.danger,
                      color: FP.color.danger,
                      borderRadius: 1.2,
                      textTransform: "none",
                      fontWeight: 700,
                      "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                    }}
                  >
                    Generar PDF
                  </Button>
                )}
              </Box>
            </Box>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
              {rows.length > 0 ? (
                <>
                  <Box sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}>
                    <DataGrid
                      rows={rowsFiltradas}
                      columns={esCancelados ? columnasCanceladas : columns}
                      getRowId={(row) => (row.id != null ? String(row.id) : `${row.norecibo ?? ""}-${row.noprest ?? ""}`)}
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      checkboxSelection
                      disableRowSelectionOnClick
                      rowHeight={42}
                      sx={{ minWidth: { xs: 720, sm: "100%" }, height: 520, ...fpDataGridSx }}
                      autoHeight={false}
                    />
                  </Box>
                  <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}>
                    {rowsFiltradas.map((row, idx) => (
                      <Box
                        key={row.id != null ? String(row.id) : `card-${idx}`}
                        sx={{
                          border: `1px solid ${FP.color.border}`,
                          borderRadius: 2,
                          p: 1.5,
                          bgcolor: "#FFFFFF",
                          boxShadow: FP.shadow.soft,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                              Doc #{row.noprest ?? row.id}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                              {row.nombres || "—"}
                            </Typography>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                              {row.fecha || "—"}
                            </Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              Cantidad
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "1rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                              {row.cantsol ?? "—"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1 }}>
                          {(esCancelados
                            ? [
                                ["Plazo", row.plazo ?? "—"],
                                ["Interés", row.interes ?? "—"],
                                ["Apellidos", row.apellidos ?? "—"],
                              ]
                            : [
                                ["Capital", `$${fmtMoney(row.vpagcap)}`],
                                ["Interés", `$${fmtMoney(row.vpagint)}`],
                                ["Mora", `$${fmtMoney(row.vpagmora)}`],
                              ]
                          ).map(([label, value]) => (
                            <Box key={label}>
                              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                {label}
                              </Typography>
                              <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, wordBreak: "break-word" }}>
                                {value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ mt: 1.2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          {!esCancelados && puedeReimprimir && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PrintOutlinedIcon fontSize="small" />}
                              disabled={reimprimiendo !== null}
                              onClick={() => reimprimir(row)}
                              sx={{ borderRadius: 1.2, textTransform: "none", fontWeight: 700 }}
                            >
                              Reimprimir
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                            onClick={() => abrirConfirmacion(row)}
                            sx={{ borderRadius: 1.2, textTransform: "none", fontWeight: 700 }}
                          >
                            Cancelar pago
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                  <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                    No hay datos para mostrar
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                    Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {presentarpdf && (
            <Dialog
              open={presentarpdf}
              maxWidth="lg"
              fullWidth
              fullScreen={isXs}
              onClose={() => setPresentarpdf(false)}
              PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
            >
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" fontWeight="bold">
                  Vista Previa del Reporte PDF
                </Typography>
                <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                  <CancelIcon />
                </Button>
              </DialogTitle>
              <DialogContent>
                <MyDocument
                  rowsData={rowsFiltradas}
                  // El controller puede venir vacío (o "false") tras rehidratar la
                  // sesión; localStorage siempre queda poblado en el login.
                  nombreNegocio1={
                    (nombreNegocio && nombreNegocio !== "false" && nombreNegocio) ||
                    localStorage.getItem("nombrenegocio") ||
                    ""
                  }
                  cancelados={esCancelados}
                />
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Cancelar pago</DialogTitle>
            <DialogContent>
              <DialogContentText>
                ¿Seguro que deseas cancelar este pago? Recibo #{pagoTarget?.norecibo || pagoTarget?.id || ""}.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>No</Button>
              <Button color="error" variant="contained" onClick={cancelarPago} disabled={cancelandoPago}>
                Sí, cancelar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
    </Box>
  );
}

export default Informepagos;
